import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  ClipboardList,
  FilePlus2,
  FileSpreadsheet,
  FileText,
  Gauge,
  Landmark,
  LayoutDashboard,
  PieChart,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  UserCog,
  Users,
  Users2,
} from 'lucide-react';
import type { Bereich } from '@/lib/use-permissions';

export interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  section?: string;
  badge?: string;
  /** BL-298: rot statt neutral -- für Zähler, die Handlungsbedarf signalisieren (z.B. Mahnwesen). */
  badgeTone?: 'default' | 'danger';
  /**
   * BL-263: Bereich für die Sperrliste (`User.gesperrteBereiche`). Fehlt bei
   * Einträgen, die unabhängig davon immer sichtbar bleiben (Übersicht,
   * Benutzerverwaltung — Letzteres bewusst, siehe requireBereich-Kommentar
   * in server.ts).
   */
  bereich?: Bereich;
}

export const NAV: NavItem[] = [
  { to: '/', label: 'Übersicht', icon: LayoutDashboard, section: 'Start' },

  { to: '/kunden', label: 'Kunden', icon: Building2, section: 'Stammdaten', bereich: 'STAMMDATEN' },
  {
    to: '/consultants',
    label: 'Mitarbeiter',
    icon: Users,
    section: 'Stammdaten',
    bereich: 'STAMMDATEN',
  },
  {
    to: '/projekte',
    label: 'Projekte',
    icon: Briefcase,
    section: 'Stammdaten',
    bereich: 'STAMMDATEN',
  },
  { to: '/rollen', label: 'Rollen', icon: UserCog, section: 'Stammdaten', bereich: 'STAMMDATEN' },

  // BL-113 (12.08.2026): haeufigste Schreibaktion hatte keinen Sidebar-/
  // Mobile-Menue-Eintrag, nur als Deep-Link von 5 anderen Seiten erreichbar.
  {
    to: '/rechnungen/neu',
    label: 'Neue Rechnung',
    icon: FilePlus2,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },
  {
    to: '/orderbuch',
    label: 'Rechnungen',
    icon: FileText,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },
  // BL-218 (26.08.2026): "Gutschriften" (~9 Einträge) hatte hier einen
  // eigenständigen Eintrag auf demselben visuellen Rang wie Rechnungen
  // (~950 Einträge) — jetzt Tab unter /orderbuch statt eigenem Sidebar-Icon,
  // siehe orderbuch-tabs-page.tsx.
  {
    to: '/abrechnungszeitraeume',
    label: 'Zeiträume',
    icon: Calendar,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },
  // BL-241 (31.08.2026): "kein Kontingent hinterlegt" war bisher nur je
  // Projekt einzeln sichtbar (project-form.tsx) — eigene Übersichtsseite,
  // damit die fehlende Zuordnung nicht länger unsichtbar bleibt.
  // BL-263: liest von /api/projects (STAMMDATEN-gate), nicht ABRECHNUNG —
  // wer STAMMDATEN gesperrt hat, sieht diese Seite trotz Abrechnung-Nav-
  // Sektion nicht mehr funktionsfähig. Dokumentierte Einschränkung, siehe
  // docs/BL-263-architektur.md.
  {
    to: '/kontingente',
    label: 'Kontingente',
    icon: PieChart,
    section: 'Abrechnung',
    bereich: 'STAMMDATEN',
  },
  // BL-202 (26.08.2026): "Zahlungsabgleich" geparkt (Fehlzuordnungsrisiko,
  // siehe App.tsx-Kommentar an der entfernten Route) — /bankkonten deckt
  // denselben Zweck bereits sicherer ab.
  {
    to: '/bankkonten',
    label: 'Bankkonten',
    icon: Landmark,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },
  {
    to: '/mahnwesen',
    label: 'Mahnwesen',
    icon: AlertTriangle,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },
  // Berichte logisch zur Abrechnung — UI-Test U11 (04.05.2026): Start hatte
  // nur Übersicht + Berichte; Berichte aggregieren Abrechnungs-Daten und
  // gehören thematisch dazu.
  {
    to: '/berichte',
    label: 'Berichte',
    icon: BarChart3,
    section: 'Abrechnung',
    bereich: 'ABRECHNUNG',
  },

  {
    to: '/personalcontrolling',
    label: 'Personalcontrolling',
    icon: Gauge,
    section: 'Auswertung',
    bereich: 'AUSWERTUNG',
  },

  // Eigener Sidebar-Abschnitt "Kanzlei" statt in "Auswertung" (User-Wunsch
  // 13.08.2026): alles hier stammt aus SuSa-/Kanzlei-Exporten, nicht aus
  // fakts eigenem Orderbuch — soll auch in der Navigation klar getrennt von
  // den Fakt-eigenen Auswertungen (Personalcontrolling, Berichte) stehen.
  // Abschnitt bewusst NICHT "FiBu" genannt (Review 19.08.2026): FiBu
  // (Finanzbuchhaltung) und LoBu (Lohnbuchhaltung) sind in der Buchhaltung
  // gleichrangige, getrennte Disziplinen — "FiBu" als Abschnittsname hätte
  // LoBu fälschlich als Unterpunkt von FiBu dargestellt. "Kanzlei" umfasst
  // beide neutral. Künftige weitere Kanzlei-Auswertungen (OPOS, USt-VA,
  // BWA, …) bekommen hier jeweils einen eigenen Nav-Eintrag im selben
  // Abschnitt.
  { to: '/ausgaben', label: 'FiBu', icon: FileSpreadsheet, section: 'Kanzlei', bereich: 'KANZLEI' },
  {
    to: '/lohnbuchhaltung',
    label: 'LoBu',
    icon: Users2,
    section: 'Kanzlei',
    bereich: 'KANZLEI',
  },
  { to: '/opos', label: 'OPOS', icon: ClipboardList, section: 'Kanzlei', bereich: 'KANZLEI' },
  { to: '/ustva', label: 'USt-VA', icon: Receipt, section: 'Kanzlei', bereich: 'KANZLEI' },

  // Label seit dem Tab-Umbau (12.08.2026) nur noch „DATEV": die Seite enthält
  // beide DATEV-Wege (CSV-Buchungsstapel + Belegversand), „DATEV-Export" hätte
  // den Versand verschwiegen und ihn dort nicht vermuten lassen.
  { to: '/datev', label: 'DATEV', icon: FileSpreadsheet, section: 'System', bereich: 'SYSTEM' },
  {
    to: '/protokoll',
    label: 'Protokoll',
    icon: FileText,
    section: 'System',
    bereich: 'COMPLIANCE',
  },
  // BL-263: bewusst KEIN `bereich` — Benutzerverwaltung bleibt für jeden
  // ADMIN immer sichtbar/erreichbar, unabhängig von `gesperrteBereiche`
  // (siehe requireBereich-Kommentar in server.ts).
  { to: '/benutzer', label: 'Benutzer', icon: ShieldCheck, section: 'System' },
  // BL-058 (18.05.2026): Compliance-Dashboard für die Betriebsprüfung
  // (GoBD-KPIs, Hard-Deletes, Rollen-Matrix, Backup-Status).
  {
    to: '/compliance',
    label: 'Compliance',
    icon: ScrollText,
    section: 'System',
    bereich: 'COMPLIANCE',
  },
  {
    to: '/einstellungen',
    label: 'Einstellungen',
    icon: Settings,
    section: 'System',
    bereich: 'SYSTEM',
  },
];

export function groupBySection(items: NavItem[]): Array<{ section: string; items: NavItem[] }> {
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    const key = item.section ?? '';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return Array.from(map.entries()).map(([section, items]) => ({ section, items }));
}
